import { useEffect, useState } from "react";
import {
  Button,
  CardHeader,
  DataGrid,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Loading,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Chip,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Icon,
} from "../../../../../components";
import {
  OrdemServicoService,
  OrdemServicosItensService,
  OrdemServicosServicosService,
  ProdutosService,
} from "../../../../../services";
import { formatDate, formatPrice, responseErros } from "../../../../../utils";
import { format } from "date-fns";
import { calcularValorTotal } from "../../add-ordem-servico";
import RenderizadorCamposValues from "../renderizador-campos-values.component";
import shortid from "shortid";

const filtrosIniciasPaginacao = {
  page: 1,
  limit: 10,
  orderBy: "updatedAt",
  order: "desc",
};

const HistorioClienteDialog = ({
  open,
  setOpen,
  clienteId,
  totalItems,
  setTotalItems,
  prestadoresList,
  statusList,
  ordemServicoEntity,
  setOrdemServico,
  campoChave,
  setListaOrdemServicoProdutos,
  setListaOrdemServicoServicos,
}) => {
  const ordemServicoService = new OrdemServicoService();
  const ordemServicoItensService = new OrdemServicosItensService();
  const ordemServicoServicosService = new OrdemServicosServicosService();
  const produtosService = new ProdutosService();

  const [loading, setLoading] = useState(false);
  const [servicos, setServicos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [ordemServicos, setOrdemServicos] = useState([]);
  const [ordemServicoSelecionada, setOrdemServicoSelecionada] = useState(null);
  const [filtros, setFiltros] = useState({
    ...filtrosIniciasPaginacao,
  });

  const [query, setQuery] = useState("");

  const colunasServicos = [
    {
      field: "codigo",
      headerName: "Código",
      flex: 100,
    },
    {
      field: "descricao",
      headerName: "Serviço",
      flex: 500,
    },
    {
      field: "prestadorServicoId",
      headerName: "P. de Servico",
      flex: 150,
      sortable: false,
      filtroPessoa: true,
      valueGetter: (params) =>
        prestadoresList?.find((item) => item.id == params.value)
          ?.nomeRazaoSocial,
    },
    {
      field: "quantidade",
      headerName: "Qtd.",
      flex: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "valor",
      headerName: "Valor (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => formatPrice(parseFloat(params.value || 0)),
    },
    {
      field: "totalItem",
      headerName: "Subtotal (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.value ? formatPrice(parseFloat(params.value)) : formatPrice(0),
    },
    {
      field: "desconto",
      headerName: "Desconto (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => formatPrice(parseFloat(params.value || 0)),
    },
    {
      field: "total",
      headerName: "Total",
      flex: 180,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => {
        return formatPrice(
          parseFloat(
            params.row.totalItem
              ? params.row.totalItem
              : params.row.valor * params.row.quantidade -
                  (params.row.desconto || 0),
          ),
        );
      },
    },
  ];

  const colunasProdutos = [
    {
      field: "codigo",
      headerName: "Cod.",
      flex: 100,
    },
    {
      field: "descricao",
      headerName: "Produto",
      flex: 400,
    },
    {
      field: "quantidade",
      headerName: "Quantidade",
      headerAlign: "center",
      align: "center",
      width: 150,
    },
    {
      field: "valorUnitario",
      headerName: "Unitário R$",
      headerAlign: "right",
      align: "right",
      flex: 200,
      valueGetter: (params) => formatPrice(parseFloat(params.value), false),
    },
    {
      field: "totalItem",
      headerName: "Total R$",
      flex: 300,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) =>
        params.value ? formatPrice(parseFloat(params.value), false) : "",
    },
  ];

  useEffect(() => {
    if (open && clienteId && ordemServicos.length === 0) {
      buscarOrdemServicos({ ...filtros, clienteId });
    }
  }, [open, clienteId]);

  const buscarOrdemServicos = async (filtrosBusca) => {
    setLoading(true);
    const result = await ordemServicoService.getAll(filtrosBusca);
    if (!result.isAxiosError) {
      setOrdemServicos(result.data.rows);
      setTotalItems(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarProdutosAvancado = (itens) => {
    const produtosIds = itens.map(({ produtoId }) => produtoId);
    const filtro = {
      id: produtosIds,
      ativado: true,
      nonPaginated: true,
    };
    produtosService.getAllProdutosSimples(filtro).then((result) => {
      if (!result.isAxiosError) {
        preencherProdutos(itens, result.data);
      } else {
        setLoading(false);
        responseErros(result);
      }
    });
  };

  const preencherProdutos = (itensBusca, produtosRetornados) => {
    const produtosPreenchidos = itensBusca.map((item) => {
      const produtoBusca = produtosRetornados.find(
        (produto) => produto.id == item.produtoId,
      );
      return {
        ...item,
        descricao: produtoBusca?.descricao,
        totalItem: calcularValorTotal(item),
      };
    });
    setLoading(false);
    setProdutos(produtosPreenchidos);
  };

  const handlePageChange = (newPage) => {
    const novosFiltros = { ...filtros, page: newPage };
    setFiltros(novosFiltros);
    buscarOrdemServicos({ ...novosFiltros, clienteId });
    setOrdemServicoSelecionada(null);
    setServicos([]);
    setProdutos([]);
  };

  const handleBusca = (e) => {
    if (e) e.preventDefault();
    const novosFiltros = { ...filtrosIniciasPaginacao };
    if (campoChave) {
      novosFiltros.campoChave = query;
    } else {
      novosFiltros.numeroDocumento = query;
    }
    setFiltros(novosFiltros);
    buscarOrdemServicos({ ...novosFiltros, clienteId });
    setOrdemServicoSelecionada(null);
    setServicos([]);
    setProdutos([]);
  };

  const handleOrdemServicoClick = async (ordemServico) => {
    setOrdemServicoSelecionada(ordemServico);
    setLoading(true);

    try {
      const resServicos = await ordemServicoServicosService.getAll({
        ordemServicoId: ordemServico.id,
        nonPaginated: true,
      });

      if (!resServicos.isAxiosError) {
        setServicos(resServicos.data);
      } else {
        responseErros(resServicos);
        setServicos([]);
      }

      const resItens = await ordemServicoItensService.getAll({
        ordemServicoId: ordemServico.id,
        nonPaginated: true,
      });

      if (!resItens.isAxiosError) {
        if (resItens.data && resItens.data.length > 0) {
          buscarProdutosAvancado(resItens.data);
        } else {
          setProdutos([]);
          setLoading(false);
        }
      } else {
        responseErros(resItens);
        setProdutos([]);
        setLoading(false);
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes da Ordem de Serviço:", error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setServicos([]);
    setProdutos([]);
    setOrdemServicoSelecionada(null);
  };

  const getStatus = (os) => {
    const statusSelectValue = os?.statusPersonalizadoId
      ? statusList.find((s) => s.id == os?.statusPersonalizadoId)
      : statusList.find((s) => s.descricao == os?.status);
    return statusSelectValue;
  };

  const copiarOrdemServico = async (osResumida) => {
    setLoading(true);
    try {
      const responseCompleta = await ordemServicoService.getById(
        osResumida?.id,
      );
      if (!responseCompleta.isAxiosError && responseCompleta.data) {
        const osCompleta = responseCompleta.data;
        ordemServicoEntity.insertDadosOrdemServico(
          osCompleta,
          osCompleta.id,
          true,
        );
        if (osCompleta.servicos?.length > 0) {
          const servicos = osCompleta?.servicos.map((item) => ({
            ...item,
            id: shortid.generate(),
          }));
          ordemServicoEntity.setChangeServicos(servicos);
          setListaOrdemServicoServicos(ordemServicoEntity?.servicos);
        }
        if (osCompleta.itens?.length > 0) {
          const itens = osCompleta?.itens.map((item) => ({
            ...item,
            id: shortid.generate(),
            totalItem:
              item.quantidade * item.valorUnitario -
              (item.desconto || 0) +
              (item.despesas || 0),
          }));
          ordemServicoEntity.setChangeItens(itens);
          setListaOrdemServicoProdutos(ordemServicoEntity?.itens);
        }
        setOrdemServico({ ...ordemServicoEntity });
        setOpen(false);
      } else {
        responseErros(responseCompleta);
      }
    } catch (error) {
      console.error("Erro ao copiar Ordem de Serviço:", error);
    } finally {
      setLoading(false);
    }
  };

  const abrirOrdemServico = (idOs) => {
    const novaUrl = window.location.pathname.replace(/create$/, idOs);
    window.open(novaUrl, "_blank");
  };

  const totalPaginas = Math.ceil(totalItems / filtros.limit);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: "75vh",
          maxHeight: "90vh",
        },
      }}
    >
      <div className="px-3">
        <CardHeader
          title="Histórico de Ordens de Serviço"
          action={
            <IconButton
              color="inherit"
              onClick={handleClose}
              sx={{
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i
                className="ph-bold ph-x"
                style={{ fontSize: "clamp(17px, 4vw, 20px)" }}
              ></i>
            </IconButton>
          }
        />
      </div>

      <DialogContent sx={{ p: 1 }}>
        <Grid container sx={{ minHeight: "60vh" }}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: "1px solid",
              borderColor: "divider",
              p: 0,
            }}
          >
            <Box sx={{ px: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ mt: 1, mb: 3 }}
              >
                Lista de Ordens de Serviço
              </Typography>

              <Box
                component="form"
                onSubmit={handleBusca}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: 1,
                  mb: 3,
                  height: "40px",
                }}
              >
                <Box sx={{ flex: 1, height: "100%" }}>
                  {campoChave ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        "& .MuiGrid-root": {
                          m: 0,
                          width: "100%",
                          height: "100%",
                        },
                        "& .MuiGrid-item": {
                          p: 0,
                          maxWidth: "100%",
                          flexBasis: "100%",
                        },
                        "& .MuiFormControl-root": { mb: 0, height: "100%" },
                        "& .MuiInputBase-root": { height: "100%" },
                      }}
                    >
                      <RenderizadorCamposValues
                        campos={[
                          {
                            ...campoChave,
                            value: query,
                            label: `Pesquisar por ${
                              campoChave?.label || "campo chave"
                            }`,
                          },
                        ]}
                        onChangeValor={(id, val) => setQuery(val)}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      label="Pesquisar por Nº Documento"
                      variant="outlined"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      sx={{
                        height: "100%",
                        "& .MuiInputBase-root": { height: "100%" },
                      }}
                    />
                  )}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    height: "50px",
                    minWidth: "100px",
                  }}
                >
                  Buscar
                </Button>
              </Box>

              {ordemServicos.length > 0 ? (
                <List sx={{ maxHeight: 400, overflowY: "auto", pr: 1 }}>
                  {ordemServicos.map((os) => {
                    const status = getStatus(os);
                    return (
                      <ListItemButton
                        key={os.id}
                        sx={{
                          borderRadius: "8px",
                          mb: 2,
                          py: 1,
                          px: 2,
                          border:
                            ordemServicoSelecionada?.id === os.id
                              ? "2px solid #2196f3"
                              : "1px solid transparent",
                          backgroundColor:
                            ordemServicoSelecionada?.id === os.id
                              ? "#e3f2fd"
                              : "white",
                          boxShadow:
                            ordemServicoSelecionada?.id === os.id
                              ? "0 4px 12px rgba(25, 118, 210, 0.15)"
                              : "0 2px 4px rgba(0, 0, 0, 0.05)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            backgroundColor:
                              ordemServicoSelecionada?.id === os.id
                                ? "#e3f2fd"
                                : "#f5f5f5",
                            transform: "translateY(-1px)",
                            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                        disableRipple
                        selected={ordemServicoSelecionada?.id === os.id}
                        onClick={() => handleOrdemServicoClick(os)}
                      >
                        <ListItemText
                          primary={`Documento: ${os?.serieDocumento || ""}/${
                            os?.numeroDocumento || ""
                          }`}
                          secondary={`Data de Criação: ${format(
                            formatDate.received(os.createdAt),
                            "dd/MM/yyyy",
                          )}`}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Tooltip
                            title="Abrir Ordem de Serviço"
                            arrow
                            placement="top"
                          >
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirOrdemServico(os?.id);
                              }}
                              size="small"
                            >
                              <i
                                className="ph-fill ph-arrow-square-out"
                                style={{ fontSize: 20 }}
                              ></i>
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title="Copiar Ordem de Serviço"
                            arrow
                            placement="top"
                          >
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                copiarOrdemServico(os);
                              }}
                              size="small"
                            >
                              <i
                                className="ph-fill ph-copy"
                                style={{ fontSize: 20 }}
                              ></i>
                            </IconButton>
                          </Tooltip>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            <Tooltip
                              title={status?.status || ""}
                              arrow
                              placement="right"
                            >
                              <Icon
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <i
                                  className="ph-fill ph-circle"
                                  style={{
                                    color: `${status?.cor}DD`,
                                    fontSize: 18,
                                  }}
                                ></i>
                              </Icon>
                            </Tooltip>
                          </Box>
                        </Box>
                      </ListItemButton>
                    );
                  })}
                </List>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "200px",
                    p: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", fontWeight: 500 }}
                  >
                    Nenhuma ordem de serviço encontrada.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              py: 1,
              px: 2,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
            }}
          >
            {ordemServicoSelecionada ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {`Detalhes da Ordem de Serviço - Documento: ${
                      ordemServicoSelecionada?.serieDocumento || ""
                    }/${ordemServicoSelecionada?.numeroDocumento || ""}`}
                  </Typography>
                </Box>

                {servicos.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                    >
                      Serviços
                    </Typography>
                    <DataGrid
                      rows={servicos}
                      columns={colunasServicos}
                      loading={false}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}

                {produtos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}
                    >
                      Produtos
                    </Typography>
                    <DataGrid
                      rows={produtos}
                      columns={colunasProdutos}
                      loading={loading}
                      disableSelectionOnClick
                      noFilter
                      disableAcoes
                      hideFooter
                      autoHeight
                    />
                  </Box>
                )}

                {servicos.length === 0 && produtos.length === 0 && !loading && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", mt: 4 }}
                  >
                    Nenhum serviço ou produto lançado nesta ordem de serviço.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  <i
                    className="ph ph-arrow-left"
                    style={{ fontSize: 36, color: "#bdbdbd" }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500, textAlign: "center" }}
                >
                  Selecione uma Ordem de Serviço
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", maxWidth: 280 }}
                >
                  Clique em uma Ordem de Serviço na lista ao lado para
                  visualizar seus serviços e produtos detalhados.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="d-flex justify-content-start m-2">
        <Box
          className="d-flex justify-content-center align-items-center gap-2"
          sx={{ px: "2.5rem" }}
        >
          <Button
            onClick={() => handlePageChange(filtros.page - 1)}
            disabled={filtros.page === 1}
          >
            Anterior
          </Button>
          <Chip
            label={` Página ${filtros.page} de ${
              totalPaginas === 0 ? 1 : totalPaginas
            }`}
          />
          <Button
            onClick={() => handlePageChange(filtros.page + 1)}
            disabled={filtros.page === totalPaginas || totalPaginas === 0}
          >
            Próxima
          </Button>
        </Box>
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
};

export default HistorioClienteDialog;
